import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import api, { getApiErrorMessage } from '../../services/api';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Truck, FileText, Search } from 'lucide-react';
import { PORTAL_API } from './constants';
import { SortableTableHeader, useSortState, sortData } from '../../components/SortableTableHeader';

const TZ_DEFAULT = 'America/Los_Angeles';

export function SupplierOrdersTab({ orders, orderSearch, setOrderSearch, onReload, token }) {
  const [dispatchOpen, setDispatchOpen] = useState(null);
  const [dispatch, setDispatch] = useState({ comment: '', dispatch_date: '', dispatch_method: '', estimated_arrival: '' });
  const [sortState, handleSort] = useSortState('order_number', 'desc');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const handleOrderAction = async (orderNum, action, extraData = {}) => {
    try {
      await api.post(`${PORTAL_API}/my-orders/${orderNum}/${action}`, extraData, authHeaders);
      toast.success(`Order ${orderNum} ${action}ed`);
      onReload();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold">My Orders</h2>
        <span className="text-xs text-slate-500">Total Records: {orders.length}</span>
      </div>
      <div className="relative mb-3 max-w-xs">
        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
        <input value={orderSearch} onChange={e => setOrderSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && onReload()}
          className="pl-9 h-9 text-sm border rounded w-full" placeholder="Search orders..." />
      </div>
      <div className="border rounded-lg bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b">
            <SortableTableHeader label="Order Number" field="order_number" sortState={sortState} onSort={handleSort} />
            <SortableTableHeader label="Issued Date" field="created_at" sortState={sortState} onSort={handleSort} />
            <SortableTableHeader label="Dispatched Date" field="dispatched_at" sortState={sortState} onSort={handleSort} />
            <SortableTableHeader label="Buyer" field="buyer_name" sortState={sortState} onSort={handleSort} />
            <SortableTableHeader label="Status" field="supplier_acknowledged" sortState={sortState} onSort={handleSort} />
            <th className="text-center px-3 py-2 font-semibold">Action</th>
          </tr></thead>
          <tbody>
            {sortData(orders, sortState.field, sortState.dir).map(o => (
              <tr key={o.order_number} className="border-b hover:bg-slate-50">
                <td className="px-3 py-2 font-mono">{o.order_number}</td>
                <td className="px-3 py-2 text-xs">{o.created_at ? new Date(o.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: TZ_DEFAULT }) : ''}</td>
                <td className="px-3 py-2 text-xs">{o.dispatched_at ? new Date(o.dispatched_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: TZ_DEFAULT }) : ''}</td>
                <td className="px-3 py-2 text-indigo-600 text-xs">{o.buyer_name}</td>
                <td className="px-3 py-2"><Badge className={`text-[10px] ${o.supplier_acknowledged === 'ACCEPTED' ? 'bg-emerald-100 text-emerald-700' : o.supplier_acknowledged === 'REJECTED' ? 'bg-red-100 text-red-700' : o.supplier_acknowledged === 'DISPATCHED' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>{o.supplier_acknowledged || 'Pending'}</Badge></td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-center gap-1">
                    {(() => {
                      const acked = o.supplier_acknowledged;
                      const done = acked === 'ACCEPTED' || acked === 'REJECTED';
                      return <>
                        <button onClick={() => !done && handleOrderAction(o.order_number, 'accept')}
                          disabled={done} className={`p-1 ${done ? 'text-slate-300 cursor-default' : 'text-emerald-600 hover:text-emerald-800'}`} title="Accept">
                          <CheckCircle className="w-4 h-4" /></button>
                        <button onClick={() => { if (!done) { const r = window.prompt('Rejection reason:'); if (r !== null) handleOrderAction(o.order_number, 'reject', { reason: r }); } }}
                          disabled={done} className={`p-1 ${done ? 'text-slate-300 cursor-default' : 'text-red-500 hover:text-red-700'}`} title="Reject">
                          <XCircle className="w-4 h-4" /></button>
                        <button onClick={() => setDispatchOpen(o.order_number)} className="text-blue-500 hover:text-blue-700 p-1" title="Dispatch Notes">
                          <Truck className="w-4 h-4" /></button>
                        <button onClick={async () => { try { const r = await api.get(`${PORTAL_API}/my-orders/${o.order_number}/pdf`, { ...authHeaders, responseType: 'blob' }); const url = URL.createObjectURL(new Blob([r.data])); window.open(url); } catch { toast.error('PDF not available'); } }}
                          className="text-red-600 hover:text-red-800 p-1" title="View PDF"><FileText className="w-4 h-4" /></button>
                      </>;
                    })()}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Dispatch Modal */}
      {dispatchOpen && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center" onClick={() => setDispatchOpen(null)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-sm mb-3">Dispatch Notes — {dispatchOpen}</h3>
            <div className="space-y-3">
              <div><label className="text-xs font-medium">Comment</label><textarea value={dispatch.comment} onChange={e => setDispatch(d => ({ ...d, comment: e.target.value }))} className="w-full border rounded p-2 text-sm h-16" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium">Dispatch Date</label><input type="date" value={dispatch.dispatch_date} onChange={e => setDispatch(d => ({ ...d, dispatch_date: e.target.value }))} className="w-full border rounded p-2 text-sm" /></div>
                <div><label className="text-xs font-medium">Dispatch Method</label><input value={dispatch.dispatch_method} onChange={e => setDispatch(d => ({ ...d, dispatch_method: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder="e.g. UPS, FedEx" /></div>
              </div>
              <div><label className="text-xs font-medium">Estimated Arrival</label><input type="date" value={dispatch.estimated_arrival} onChange={e => setDispatch(d => ({ ...d, estimated_arrival: e.target.value }))} className="w-full border rounded p-2 text-sm" /></div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setDispatchOpen(null)}>Cancel</Button>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={async () => {
                  await handleOrderAction(dispatchOpen, 'dispatch', dispatch);
                  setDispatchOpen(null); setDispatch({ comment: '', dispatch_date: '', dispatch_method: '', estimated_arrival: '' });
                }}>Save Dispatch</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
